import { IGameSystem } from './IGameSystem';
import { DevConfig } from '../../config/environment/devConfig';
import { gameLogger } from '../../utils/GameLogger';
import type { ServiceMap, ServiceId } from './ServiceMap';

/**
 * ServiceRegistry
 * A type-safe Service Locator for managing game system instances and dependencies.
 */
export class ServiceRegistry {
  private static instance: ServiceRegistry;
  private services: Map<string, any> = new Map();
  private systems: Map<string, IGameSystem> = new Map();
  private bootComplete = false;

  private constructor() { }

  /**
     * Get the singleton instance of the registry
     */
  public static getInstance(): ServiceRegistry {
    if (!ServiceRegistry.instance) {
      ServiceRegistry.instance = new ServiceRegistry();
    }
    return ServiceRegistry.instance;
  }

  /**
     * Register a service/system
     * @param id Unique identifier
     * @param service The service instance
     */
  public register<T>(id: string, service: T): void {
    if (this.services.has(id) && DevConfig.logging.registry) {
      gameLogger.warn('registry', `[ServiceRegistry] Overwriting existing service: ${id}`);
    }
    this.services.set(id, service);

    // If it matches the IGameSystem interface, track it as a system too
    if (this.isGameSystem(service)) {
      this.systems.set(id, service as unknown as IGameSystem);
    }
    if (DevConfig.logging.registry) {
      gameLogger.info('registry', `[ServiceRegistry] Registered: ${id}`);
    }
  }

  /**
     * Get a registered service.
     *
     * @param id Unique identifier
     * @param opts.optional When true, return undefined silently if the service
     *   isn't registered. Use this for presets that intentionally disable a
     *   system (e.g. `live-lobby` has `mobs: false` → MobSystem isn't
     *   registered, but CombatSystem + EntitySystem still reference it via a
     *   null-safe `mobSystem?.getManager()`). Without `optional: true` the
     *   registry emits a red toast even though the absence is expected by
     *   design. Defaults to false for back-compat — unknown callers still
     *   get loud warnings for real initialization-order bugs.
     */
  public get<K extends ServiceId>(id: K, opts?: { optional?: boolean }): ServiceMap[K] | undefined {
    const service = this.services.get(id);
    if (!service && this.bootComplete && !opts?.optional) {
      // Only warn after boot — during boot, services register in order and early queries are expected
      gameLogger.warn('registry', `[ServiceRegistry] Missing service: ${id}`);
      (globalThis as any).__errorToast?.('warn', `Missing service: ${id}`);
    }
    return service as ServiceMap[K] | undefined;
  }

  /** Call after all systems are registered. Enables missing-service warnings. */
  public markBootComplete(): void {
    this.bootComplete = true;
    gameLogger.info('registry', `[ServiceRegistry] Boot complete — ${this.services.size} services, ${this.systems.size} systems`);
  }

  public isBootComplete(): boolean {
    return this.bootComplete;
  }

  /** Get all registered service names */
  public getServiceNames(): string[] {
    return Array.from(this.services.keys()).sort();
  }

  /**
     * Check if a service is registered
     */
  public has(id: string): boolean {
    return this.services.has(id);
  }

  /**
     * Get all registered systems (implementing IGameSystem)
     */
  public getAllSystems(): IGameSystem[] {
    return Array.from(this.systems.values());
  }

  /**
     * Clear all services (for restart/testing)
     */
  public clear(): void {
    this.services.clear();
    this.systems.clear();
    gameLogger.info('registry', '[ServiceRegistry] Cleared all services');
  }

  // Type guard for IGameSystem
  private isGameSystem(arg: any): arg is IGameSystem {
    return arg && typeof arg.update === 'function' && typeof arg.initialize === 'function';
  }
}
